import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./split-tests.server";

const schema = z.object({
  session_id: z.string().max(120).nullable().optional(),
  calendar: z.enum(["intro", "demo", "unknown"]),
  tier: z.string().max(10).nullable().optional(),
  outcome: z.string().max(40).nullable().optional(),
  schedule_fired: z.boolean(),
  schedule_skipped_reason: z.string().max(200).nullable().optional(),
  email: z.string().max(320).nullable().optional(),
  phone: z.string().max(60).nullable().optional(),
  first_name: z.string().max(120).nullable().optional(),
  last_name: z.string().max(120).nullable().optional(),
  redirect_url: z.string().max(2000),
  params: z.record(z.string(), z.string()).default({}),
  missing_params: z.array(z.string().max(60)).default([]),
  page_url: z.string().max(2000).nullable().optional(),
});

/**
 * Records the post-booking verification snapshot: which calendar/tier was
 * booked, whether the Meta Schedule conversion fired, and the exact email +
 * UTM values appended to the thank-you redirect. Never throws — a logging
 * failure must never block the redirect.
 */
export const logBookingRedirect = createServerFn({ method: "POST" })
  .inputValidator((input) => schema.parse(input))
  .handler(async ({ data }) => {
    try {
      const { getRequestHeader } = await import("@tanstack/react-start/server");
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("booking_redirect_events").insert({
        session_id: data.session_id ?? null,
        calendar: data.calendar,
        tier: data.tier ?? null,
        outcome: data.outcome ?? null,
        schedule_fired: data.schedule_fired,
        schedule_skipped_reason: data.schedule_skipped_reason ?? null,
        email: data.email ?? null,
        phone: data.phone ?? null,
        first_name: data.first_name ?? null,
        last_name: data.last_name ?? null,
        redirect_url: data.redirect_url,
        params: data.params,
        missing_params: data.missing_params,
        page_url: data.page_url ?? null,
        user_agent: getRequestHeader("user-agent") ?? null,
      });
      return { ok: true as const };
    } catch (err) {
      console.error("[booking-redirect] log failed", err);
      return { ok: false as const };
    }
  });

/** Admin read: most recent booking redirect verifications. */
export const listBookingRedirects = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("booking_redirect_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return { events: data ?? [] };
  });
